<template>
  <div class="gs-affiliates-step">
    <section class="gs-wizard-card gs-affiliates-step__summary">
      <h2>Affiliate program</h2>
      <p>{{ $t('frontend_product_step9_configure') }}</p>
      <div class="gs-affiliates-step__cards">
        <button
          v-for="affiliateSettingsType in affiliateSettingsTypes"
          :key="affiliateSettingsType.id"
          type="button"
          class="gs-affiliates-step__card"
          :aria-pressed="localModel.affiliates_enabled === affiliateSettingsType.id"
          :class="{ 'is-active': localModel.affiliates_enabled === affiliateSettingsType.id }"
          @click="setAffiliateProgramState(affiliateSettingsType.id)"
        >
          <span class="gs-affiliates-step__radio" aria-hidden="true"></span>
          <span class="gs-affiliates-step__card-copy">
            <strong>{{ affiliateSettingsType.title }}</strong>
            <span>{{ affiliateSettingsType.description }}</span>
          </span>
        </button>
      </div>
      <gs-button
        v-if="localModel.affiliates_enabled"
        type="button"
        variant="primary"
        class="gs-affiliates-step__manage-button"
        @click="showAffiliateEditor = true"
      >
        Manage Affiliate Program
      </gs-button>
    </section>

      <!-- {{localModel.commission_after_processing}} -->
      <gs-drawer
        v-if="localModel.affiliates_enabled"
        v-model="showAffiliateEditor"
        size="xl"
        class="gs-affiliates-step__drawer"
        :title="$t('frontend_product_step9_affiliates')"
        subtitle="Manage commission rules, affiliate pages, integrations, and data sharing for this product."
      >
      <div class="gs-affiliates-step__editor">
        <div class="gs-affiliates-step__workspace-tabs" role="tablist" aria-label="Affiliate settings sections">
          <button type="button" role="tab" :aria-selected="editorSection === 'price-points'" :class="{ 'is-active': editorSection === 'price-points' }" @click="editorSection = 'price-points'">
            <strong>Commission rules</strong>
            <span>{{ affiliatePricePointRows.length }} price points</span>
          </button>
          <button type="button" role="tab" :aria-selected="editorSection === 'advanced'" :class="{ 'is-active': editorSection === 'advanced' }" @click="editorSection = 'advanced'">
            <strong>Advanced affiliate settings</strong>
            <span>Pages, integrations, and data sharing</span>
          </button>
        </div>
        <div class="gs-affiliates-step__workspace-body">
        <div v-if="editorSection === 'price-points'" class="gs-affiliates-step__commission-pane" role="tabpanel">
        <section class="gs-affiliates-step__default-panel">
          <div class="gs-affiliates-step__processing-row">
            <div>
              <strong>{{ $t('frontend_groovesell_products_step9_calc_commission_question') }}</strong>
              <span>Apply processing-fee handling before affiliate commissions are calculated.</span>
            </div>
            <gd-toggle aria-label="Calculate commissions after processing fees" v-model="localModel.commission_after_processing" />
          </div>
          <p v-if="localModel.commission_after_processing" class="gs-affiliates-step__notice">
            <span>{{ $t('frontend_note') }}:</span>
            {{ $t('frontend_groovesell_products_step9_commission_after_processing') }}
            <button type="button" @click="goToSetupGateways">{{ $t('frontend_groovesell_products_step9_here') }}</button>.
          </p>

          <div class="gs-affiliates-step__default-card">
            <div>
              <small>Default affiliate commission</small>
              <strong>{{ defaultCommissionTitle }}</strong>
              <p>Use this rule for price points without an override. Older saved payouts needing an update are shown below.</p>
            </div>
            <div class="gs-affiliates-step__default-stats">
              <span>
                <small>Tier 1</small>
                <strong>{{ commissionLabel(defaultCommissionTier, 'sales_commission', 'commission') }}</strong>
              </span>
              <span>
                <small>Tier 2</small>
                <strong>{{ commissionLabel(defaultCommissionTier, 'sales_commission', 'jv_broker_commission') }}</strong>
              </span>
              <span>
                <small>Recurring rules</small>
                <strong>{{ defaultRecurringCount }}</strong>
              </span>
            </div>
            <gs-button type="button" variant="outline" size="sm" @click="openDefaultCommissionModal">Edit defaults</gs-button>
          </div>
        </section>

        <section class="gs-affiliates-step__price-points">
          <div class="gs-affiliates-step__list-head">
            <strong>Price points</strong>
            <span>Apply the default commission or create a price-point override.</span>
          </div>

          <p v-if="affiliateDefaultNotice" class="gs-affiliates-step__inline-notice">{{ affiliateDefaultNotice }}</p>
          <div v-if="outOfSyncDefaultPricePoints.length" class="gs-affiliates-step__sync-notice" role="status">
            <div>
              <strong>{{ outOfSyncDefaultPricePoints.length }} price point{{ outOfSyncDefaultPricePoints.length === 1 ? '' : 's' }} still have different saved commissions.</strong>
              <span>The default shown above is not yet applied to these price points. Apply it, then save this product to update affiliate payouts.</span>
            </div>
            <gs-button type="button" variant="primary" size="sm" @click="openDefaultBulkChoice">Apply default to {{ outOfSyncDefaultPricePoints.length }}</gs-button>
          </div>

          <div class="gs-affiliates-step__group">
            <price-point-groups :price-points="localModel.pricing_points">
              <template v-slot="{ pricePoint, index }">
            <article
              :key="pricePoint.id || index"
              class="gs-affiliates-step__accordion"
              :class="{ 'is-open': expandedAffiliatePricePointIndex === index, 'has-override': hasCommissionOverride(pricePoint) }"
            >
              <div
                class="gs-affiliates-step__accordion-head"
                role="button"
                tabindex="0"
                :aria-expanded="expandedAffiliatePricePointIndex === index"
                @click="toggleAffiliatePricePoint(index)"
                @keydown.enter.prevent="toggleAffiliatePricePoint(index)"
                @keydown.space.prevent="toggleAffiliatePricePoint(index)"
              >
                <span class="gs-affiliates-step__status-dot"></span>
                <span class="gs-affiliates-step__title-block">
                  <span class="gs-affiliates-step__name">{{ pricePoint.name }}</span>
                  <span class="gs-affiliates-step__summary-line">{{ affiliatePricePointSummary(pricePoint) }}</span>
                </span>
                <button
                  type="button"
                  class="gs-affiliates-step__default-toggle gs-affiliates-step__default-toggle--row"
                  role="switch"
                  :aria-checked="isApplyingDefault(pricePoint)"
                  :aria-label="`Apply default to ${pricePoint.name}`"
                  @click.stop="setAffiliateDefaultUsage(pricePoint, !isApplyingDefault(pricePoint))"
                  @keydown.stop
                >
                  <span>Apply default</span>
                  <span class="gs-affiliates-step__switch" :class="{ 'is-on': isApplyingDefault(pricePoint) }"></span>
                </button>
                <span class="gs-affiliates-step__pill" :class="{ 'is-unsynced': isDefaultOutOfSync(pricePoint) }">{{ hasCommissionOverride(pricePoint) ? 'Override' : isDefaultOutOfSync(pricePoint) ? 'Needs update' : 'Default' }}</span>
                <span class="gs-affiliates-step__chevron">
                  <gs-icon :name="expandedAffiliatePricePointIndex === index ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
                </span>
              </div>
              <div v-if="expandedAffiliatePricePointIndex === index" class="gs-affiliates-step__accordion-body">
                <div class="gs-affiliates-step__body-head">
                  <div>
                    <strong>{{ hasCommissionOverride(pricePoint) ? 'Custom commission override' : isDefaultOutOfSync(pricePoint) ? 'Different saved commission' : 'Using default commission' }}</strong>
                    <p>{{ hasCommissionOverride(pricePoint) ? 'This price point uses custom commission rules.' : isDefaultOutOfSync(pricePoint) ? 'Apply the default above and save this product to update its payout.' : 'This price point applies the default affiliate settings.' }}</p>
                  </div>
                </div>
                <div class="gs-affiliates-step__detail-grid">
                  <div>
                    <small>Tier 1 commission</small>
                    <strong>{{ pricePointTierLabel(pricePoint, 'commission') }}</strong>
                  </div>
                  <div>
                    <small>Tier 2 commission</small>
                    <strong>{{ pricePointTierLabel(pricePoint, 'jv_broker_commission') }}</strong>
                  </div>
                  <div>
                    <small>Availability</small>
                    <strong>{{ commissionAvailabilityLabel(pricePoint) }}</strong>
                  </div>
                </div>
                <div v-if="hasCommissionOverride(pricePoint)" class="gs-affiliates-step__inline-override">
                  <label>
                    <span>Tier 1 override</span>
                    <input :value="commissionOverridesFor(pricePoint)[0].sales_commission.commission" type="number" min="0" aria-label="Tier 1 override" @input="updateInlineCommission(pricePoint, 'commission', $event.target.value)" />
                  </label>
                  <label>
                    <span>Tier 2 override</span>
                    <input :value="commissionOverridesFor(pricePoint)[0].sales_commission.jv_broker_commission" type="number" min="0" aria-label="Tier 2 override" @input="updateInlineCommission(pricePoint, 'jv_broker_commission', $event.target.value)" />
                  </label>
                  <gs-button type="button" variant="outline" size="sm" @click="openPricePointOverride(index)">Advanced override settings</gs-button>
                </div>
                <div v-else class="gs-affiliates-step__override-actions">
                  <gs-button type="button" variant="primary" size="sm" @click="startInlineOverride(pricePoint)">Edit commission inline</gs-button>
                </div>
                <button type="button" class="gs-affiliates-step__no-commission" @click="setNoCommission(pricePoint)">
                  Don’t pay commissions on this price point
                </button>
              </div>
            </article>
              </template>
            </price-point-groups>
          </div>
        </section>
        </div>

        <div v-if="editorSection === 'advanced'" ref="affiliateAdvancedPanel" class="gs-affiliates-step__advanced" role="tabpanel">
          <nav class="gs-affiliates-step__rail" aria-label="Advanced affiliate settings">
            <button
              v-for="tab in affiliateSettingsTabs"
              :key="tab.value"
              type="button"
              class="gs-affiliates-step__rail-item"
              :class="{
                'is-active': affiliateSettingsTab === tab.value,
                'has-error': affiliateSettingsErrorTabs.includes(tab.value),
              }"
              :aria-current="affiliateSettingsTab === tab.value ? 'page' : null"
              @click="handleAffiliateSettingsTabChange(tab.value)"
            >
              <strong>{{ tab.title }}</strong>
              <span>{{ tab.description }}</span>
            </button>
          </nav>
          <div class="gs-affiliates-step__advanced-content">
          <jv-blog v-if="affiliateSettingsTab === 'jvblog'" :model="localModel" :validation="$v"
            @update-model="updateModel" />
          <integrations v-else-if="affiliateSettingsTab === 'integrations'" :model="localModel" :validation="$v"
            @update-model="updateModel" :pluginSiteData="pluginSiteData" :availableIntegrations="availableIntegrations"
            :showAffiliateRegistrationIntegrationBox="showAffiliateRegistrationIntegrationBox"
            :deleteAffiliateRegistrationIntegration="openDeleteAffiliateRegistrationIntegrationConfirmModal" />
          <commissions v-else-if="affiliateSettingsTab === 'commissions'" @update-model="updateModel" :validation="$v"
            :model="localModel" />
          <commission-overrides v-else-if="affiliateSettingsTab === 'commissionOverrides'" @update-model="updateModel"
            :validation="$v" :model="localModel" :openCommissionOverridesAffModal="openCommissionOverridesAffModal"
            :focus-price-point-index="selectedPricePointIndex" />

          <affiliates-landing-pages v-else-if="affiliateSettingsTab === 'affiliatesLandingPages'"
            @update-model="updateModel" :validation="$v" :model="localModel"
            :openCustomLandingPagesAffModal="openCustomLandingPagesAffModal"
            @create-affiliate-landing-page="handleCreateAffiliateLandingPage"
            @delete-affiliate-landing-page="openDeleteAffiliateLandingPageConfirmModal" />

          <data-sharing v-else-if="affiliateSettingsTab === 'data_sharing'" @update-model="updateModel"
            :model="localModel" />
          </div>
        </div>
        </div>
      </div>
      </gs-drawer>

      <gs-modal v-model="showDefaultBulkChoice" title="Apply default commissions?" size="sm">
        <div class="gs-affiliates-step__bulk-choice">
          <p>{{ customAffiliatePricePoints.length }} price point{{ customAffiliatePricePoints.length === 1 ? '' : 's' }} have custom overrides. Choose whether to keep those overrides or replace them with the default commission.</p>
          <div class="gs-affiliates-step__bulk-choice-actions">
            <gs-button type="button" variant="outline" @click="applyDefaultToAllPricePoints(false)">Keep overrides</gs-button>
            <gs-button type="button" variant="primary" @click="applyDefaultToAllPricePoints(true)">Apply to all and remove overrides</gs-button>
          </div>
        </div>
      </gs-modal>

      <div
        v-if="showDefaultCommissionModal"
        class="gs-affiliates-step__modal-backdrop"
        @click="closeDefaultCommissionModal"
      ></div>
      <section v-if="showDefaultCommissionModal" class="gs-affiliates-step__default-modal" role="dialog" aria-modal="true" aria-labelledby="gs-affiliates-default-title">
        <header>
          <div>
            <h3 id="gs-affiliates-default-title">Default affiliate settings</h3>
            <p>These rules apply to every price point that is set to apply the default affiliate program.</p>
          </div>
          <gs-button type="button" variant="ghost" size="icon" aria-label="Close default affiliate settings" @click="closeDefaultCommissionModal">
            <gs-icon name="x"></gs-icon>
          </gs-button>
        </header>

        <div v-if="defaultCommissionDraft" class="gs-affiliates-step__modal-content">
          <section class="gs-affiliates-step__modal-card">
            <div class="gs-affiliates-step__modal-card-head">
              <div>
                <strong>Standard commission</strong>
                <p>Set the normal Tier 1 commission and optional Tier 2 commission.</p>
              </div>
              <label class="gs-affiliates-step__complex-toggle">
                <span>Complex rules</span>
                <gd-toggle aria-label="Use complex recurring commission rules" v-model="defaultCommissionComplex" />
              </label>
            </div>
            <div class="gs-affiliates-step__modal-grid">
              <label>
                <span>Tier 1 affiliate</span>
                <div class="gs-affiliates-step__modal-pair">
                  <input v-model="defaultPrimaryCommission.commission" type="number" min="0" />
                  <select v-model="defaultPrimaryCommission.commission_type">
                    <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                </div>
              </label>
              <label>
                <span>Optional Tier 2</span>
                <div class="gs-affiliates-step__modal-pair">
                  <input v-model="defaultPrimaryCommission.jv_broker_commission" type="number" min="0" />
                  <select v-model="defaultPrimaryCommission.jv_broker_commission_type">
                    <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                </div>
              </label>
            </div>
          </section>

          <section v-if="defaultCommissionComplex" class="gs-affiliates-step__modal-card">
            <div class="gs-affiliates-step__modal-card-head">
              <div>
                <strong>Recurring schedule</strong>
                <p>Use ranges to define the first set of rebills, the next set, and what happens after that.</p>
              </div>
              <span title="Leave the last row without a payment count for all remaining payments. Set its commission to 0 if payments should stop after the earlier schedule rows.">?</span>
            </div>
            <div class="gs-affiliates-step__modal-schedule">
              <div
                v-for="(commissionDetail, commissionDetailIndex) in defaultCommissionDraft.rebill_commissions"
                :key="commissionDetailIndex"
                class="gs-affiliates-step__modal-row"
              >
                <div>
                  <small>{{ recurringRangeLabel(defaultCommissionDraft.rebill_commissions, commissionDetailIndex) }}</small>
                  <strong>{{ recurringRangeDescription(defaultCommissionDraft.rebill_commissions, commissionDetailIndex) }}</strong>
                </div>
                <label>
                  <span>Payments</span>
                  <input v-model="commissionDetail.no_of_payments" type="number" min="1" placeholder="All remaining" />
                </label>
                <label>
                  <span>Tier 1</span>
                  <div class="gs-affiliates-step__modal-pair">
                    <input v-model="commissionDetail.commission" type="number" min="0" />
                    <select v-model="commissionDetail.commission_type">
                      <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                  </div>
                </label>
                <label>
                  <span>Tier 2</span>
                  <div class="gs-affiliates-step__modal-pair">
                    <input v-model="commissionDetail.jv_broker_commission" type="number" min="0" />
                    <select v-model="commissionDetail.jv_broker_commission_type">
                      <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                  </div>
                </label>
                <gs-button
                  v-if="defaultCommissionDraft.rebill_commissions.length > 1"
                  type="button"
                  variant="ghost"
                  size="icon"
                  class="is-destructive"
                  aria-label="Remove this payment group"
                  @click="deleteDefaultRecurringCommission(commissionDetailIndex)"
                >
                  <gs-icon name="trash" size="16"></gs-icon>
                </gs-button>
              </div>
            </div>
            <div class="gs-affiliates-step__modal-actions">
              <gs-button type="button" variant="primary" size="sm" @click="addDefaultRecurringCommission">Add next payment group</gs-button>
              <gs-button type="button" variant="outline" size="sm" @click="stopDefaultRecurringCommissionAfterSchedule">No commission after this schedule</gs-button>
            </div>
          </section>
        </div>

        <footer>
          <gs-button type="button" variant="primary" @click="applyDefaultCommissionModal">Apply settings</gs-button>
        </footer>
      </section>

    <gs-modal v-model="showAffiliateRegistrationIntegrationModal"
      :title="$t('frontend_groovemember_step9_add_new_integration')" size="xl">
      <div class="flex flex-col m-8">
        <div class="flex flex-col w-full mb-6">
          <div class="w-full flex flex-col mr-2">
            <label class="font-medium text-sm text-grey-700 mb-2">{{
              $t('frontend_type')
            }}</label>
            <gd-dropdown :v-bind="integrationType" :value="integrationType" :options="availableIntegrations" size="full"
              @change="setValue($event)" @keyup="setValue($event)" />
          </div>
          <div v-if="integrationType && integrationType != ''" class="w-full">
            <config-menu :key="configMenuKey" :initial-values="componentConfigValues" :plugin-id="pluginId"
              :resources-path="resourcesPath" :server-path="serverPath" :plugin="integrationType"
              :app-data="pluginSiteData" layout="vertical" :is-dark="false" :show-button-borders="false"
              :show-notifications-on-save="false" :btn-label="$t('frontend_sites_integration_modal_add_integration')"
              :opened-from="'groovesell'" @update="addAffiliateRegistrationIntegration"
              @menu-updated="configMenuItemsUpdated">
            </config-menu>
          </div>
        </div>
      </div>
    </gs-modal>

    <gs-modal v-model="showCommissionOverridesAffiliatesModal" :title="$t('frontend_affiliates')" size="xl">
      <div class="px-4">
        <div class="flex pt-2 pr-2">
          <div class="w-1/2 flex flex-row pl-2">
            <affiliates-auto-suggest :funnel="localModel.funnel_id" :excluded="excludedCommissionTierAffiliates"
              @onAffiliateAdded="addedAffiliateToCommissionTier" />
          </div>
        </div>
        <div class="flex mt-2 pt-2 pr-2">
          <div class="w-full flex flex-row">
            <affiliate-list-view :funnel="localModel.funnel_id" :affiliate-ids="selectedCommissionTierAffiliates"
              @onAffiliateRemoved="removedAffiliateFromCommissionTier" />
          </div>
        </div>
      </div>
    </gs-modal>

    <landing-page-affiliate-picker v-if="selectedLandingPage" v-model="showCustomLandingPagesAffiliatesModal"
      :funnel="localModel.funnel_id" :affiliates="selectedLandingPage.assigned_to" :page-name="selectedLandingPage.description"
      @apply="applyLandingPageAffiliates" />
    <gs-alert-dialog
      v-model="showDeleteAffiliateLandingPageConfirm"
      title="Delete affiliate landing page?"
      :copy="$t('frontend_non_revertable')"
      confirm-label="Delete"
      cancel-label="Cancel"
      @confirm="deleteAffiliateLandingPage"
    />
    <gs-alert-dialog
      v-model="showDeleteAffiliateRegistrationIntegrationConfirm"
      title="Delete affiliate integration?"
      :copy="$t('frontend_non_revertable')"
      confirm-label="Delete"
      cancel-label="Cancel"
      @confirm="deleteAffiliateRegistrationIntegration"
    />
  </div>
</template>

<script>
import {
  required,
  requiredIf,
  minLength,
  maxLength,
  numeric,
  decimal,
  url,
  minValue,
} from 'vuelidate/lib/validators';
import { ModelSelect } from 'vue-search-select';
import ConfigMenu from '@groovepages/gd-ui-kit/components/PluginMenu';
import AffiliatesAutoSuggest from '@/components/AffiliatesAutoSuggest';
import AffiliateListView from '@/components/AffiliateListView';

import Commissions from './tabs/affiliates/Commissions';
import CommissionOverrides from './tabs/affiliates/CommissionOverrides';
import AffiliatesLandingPages from './tabs/affiliates/AffiliatesLandingPages';
import LandingPageAffiliatePicker from './tabs/affiliates/LandingPageAffiliatePicker.vue';
import DataSharing from './tabs/affiliates/DataSharing';
import JvBlog from './tabs/affiliates/JvBlog';
import Integrations from './tabs/affiliates/Integrations';
import './tabs/affiliates/affiliate-settings.css';
import PricePointGroups from '../components/PricePointGroups';
import { commissionRulesMatch, copyDefaultCommission } from './affiliate-defaults';


export default {
  name: 'Step9',
  components: {
    ModelSelect,
    ConfigMenu,
    AffiliatesAutoSuggest,
    AffiliateListView,
    Commissions,
    CommissionOverrides,
    AffiliatesLandingPages,
    LandingPageAffiliatePicker,
    DataSharing,
    JvBlog,
    Integrations,
    PricePointGroups,
  },
  props: {
    model: {
      required: true,
    },
    affiliates: {
      required: true,
    },
    resourcesPath: {
      required: true,
    },
    serverPath: {
      required: true,
    },
    pluginSiteData: {
      required: true,
    },
    availableIntegrations: {
      required: true,
    },
  },
  data() {
    return {
      affiliateLandingPageToDeleteIndex: null,
      affiliateRegistrationIndex: null,
      localModel: this.model,
      commissionsIndex: 0,
      affiliateLandingPageIndex: 0,
      affiliateSettingsTypes: [
        {
          id: 0,
          title: this.$t('frontend_product_step9_no_affiliate_program'),
          description: this.$t('frontend_product_step9_no_affiliate_program_desc'),
        },
        {
          id: 1,
          title: this.$t('frontend_product_step9_activate_affiliate_program'),
          description: this.$t('frontend_product_step9_activate_affiliate_program_desc'),
        },
      ],
      affiliateSettingsTab: 'commissions',
      editorSection: 'price-points',
      showAffiliateEditor: false,
      showDefaultCommissionModal: false,
      showDefaultBulkChoice: false,
      defaultCommissionDraft: null,
      showAffiliateRegistrationIntegrationModal: false,
      showCommissionOverridesAffiliatesModal: false,
      showCustomLandingPagesAffiliatesModal: false,
      showDeleteAffiliateLandingPageConfirm: false,
      showDeleteAffiliateRegistrationIntegrationConfirm: false,
      defaultCommissionComplex: false,
      expandedAffiliatePricePointIndex: 0,
      affiliateDefaultNotice: '',

      checkoutTab: 'lookfeel',
      pricePointsTab: '',
      commissionOverridePricePointsTab: '',
      landingPagesTab: '',
      commissionOverrideSelectedAffiliate: '',
      integrationType: '',
      configMenuKey: 1,
      componentConfigValues: [],
      pluginId: '',
      integrationType: '',
      pluginMenuItems: [],
      selectedPricePointIndex: 0,
      selectedCommissionTierIndex: 0,
      selectedLandingPageIndex: 0,
      test: true,
    };
  },
  validations: {
    localModel: {
      jv_page: {
        url,
      },
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          commission_details: {
            $each: {
              name: {
                required: requiredIf(function () {
                  return this.affiliateSettingsTab == 'commissionOverrides';
                }),
              },
              trial_commission: {
                commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      
                      return regexp.test(value) && value <= 100;
                    }
                  
                    return regexp.test(value);
                  },
                },
                jv_broker_commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      
                      return regexp.test(value) && value <= 100;
                    }
                   
                    return regexp.test(value);
                  },
                },
              },
              sales_commission: {
                commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      return regexp.test(value) && value <= 100;
                    }
                    return regexp.test(value);
                  },
                },
                jv_broker_commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      return regexp.test(value) && value <= 100;
                    }
                    return regexp.test(value);
                  },
                },
              },
              rebill_commissions: {
                $each: {
                  commission: {
                    required,
                    decimal,
                    minValue: minValue(0),
                    isValidCommission(value, object) {
                      const regexp = /^\d+(\.\d{1,2})?$/;

                      if (object.commission_type == 'percent') {
                        return regexp.test(value) && value <= 100;
                      }
                      return regexp.test(value);
                    },
                  },
                  no_of_payments: {
                    numeric,
                    minValue: minValue(0),
                  },
                  jv_broker_commission: {
                    required,
                    decimal,
                    minValue: minValue(0),
                    isValidCommission(value, object) {
                      const regexp = /^\d+(\.\d{1,2})?$/;

                      if (object.commission_type == 'percent') {
                        return regexp.test(value) && value <= 100;
                      }
                      return regexp.test(value);
                    },
                  },
                },
              },
            },
          },
          post_sale_details: {
            ipn_url: {
              url,
            },
          },
        },
      },
      waiting_period: {
        required,
        numeric,
        minValue: minValue(0),
      },
      affiliate_landing_pages: {
        required,
        minLength: minLength(1),
        $each: {
          landing_page: {
            url,
          },
          description: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
        },
      },
    },
  },
  computed: {
    landingPageAssignedType() {
      const currentlyActiveLandingPageId = parseInt(
        this.landingPagesTab.split('_')[2]
      );

      const selectedLandingPageIndex = this.localModel.affiliate_landing_pages
        .map((item) => item.id)
        .indexOf(currentlyActiveLandingPageId);

      if (selectedLandingPageIndex != -1) {
        const assignedTo =
          this.localModel.affiliate_landing_pages[selectedLandingPageIndex]
            .assigned_to;

        if (assignedTo.length > 0 && assignedTo[0].id == 0) {
          return 1;
        }
        return 2;
      }

      return 2;
    },

    selectedLandingPage() { return this.localModel.affiliate_landing_pages[this.selectedLandingPageIndex]; },

    selectedCommissionTierAffiliates() {
      if (
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details.filter((obj) => {
          return obj.type != 1;
        }).length > 0 &&
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details.filter((obj) => {
          return obj.type != 1;
        })[this.selectedCommissionTierIndex] &&
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details.filter((obj) => {
          return obj.type != 1;
        })[this.selectedCommissionTierIndex].assigned_to
      ) {
        return this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details.filter((obj) => {
          return obj.type != 1;
        })[this.selectedCommissionTierIndex].assigned_to;
      }
    },

    excludedCommissionTierAffiliates() {
      const allCommissionOverrides = this.localModel.pricing_points[
        this.selectedPricePointIndex
      ].commission_details.filter((obj) => {
        return obj.type != 1;
      });

      let excludedCommissionTierAffiliates = [];

      for (let i = 0; i < allCommissionOverrides.length; i++) {
        excludedCommissionTierAffiliates =
          excludedCommissionTierAffiliates.concat(
            allCommissionOverrides[i].assigned_to
          );
      }

      return excludedCommissionTierAffiliates;
    },

    affiliatesForCommissionOverrides() {
      const affiliatesDataForCommissionOverrides = [];
      for (let i = 0; i < this.affiliates.length; i++) {
        affiliatesDataForCommissionOverrides.push({
          value: this.affiliates[i].id,
          text: this.affiliates[i].name,
        });
      }

      return affiliatesDataForCommissionOverrides;
    },

    affiliatesForLandingPages() {
      const affiliatesDataForLandingPages = [];
      affiliatesDataForLandingPages.push({
        id: 0,
        name: this.$t('frontend_affiliates_all_affiliates'),
      });
      for (let i = 0; i < this.affiliates.length; i++) {
        affiliatesDataForLandingPages.push(this.affiliates[i]);
      }

      return affiliatesDataForLandingPages;
    },
    defaultCommissionTier() {
      const pricePoint = this.localModel.pricing_points.find((item) => {
        return item.commission_details && item.commission_details.some((commission) => {
          return commission.type == 1;
        });
      });

      if (!pricePoint) {
        return null;
      }

      return pricePoint.commission_details.find((commission) => {
        return commission.type == 1;
      });
    },
    defaultCommissionTitle() {
      return this.defaultCommissionTier ? 'Standard affiliate rule' : 'No default commission set';
    },
    defaultRecurringCount() {
      if (!this.defaultCommissionTier || !this.defaultCommissionTier.rebill_commissions) {
        return 0;
      }

      return this.defaultCommissionTier.rebill_commissions.length;
    },
    commissionTypeOptions() {
      return [
        { value: 'percent', text: this.$t('frontend_product_step9_percentage') },
        { value: 'fixed', text: this.$t('frontend_product_step9_fixed_amount') },
      ];
    },
    defaultPrimaryCommission() {
      if (!this.defaultCommissionDraft) {
        return {};
      }

      return this.defaultCommissionDraft.sales_commission;
    },
    outOfSyncDefaultPricePoints() {
      return this.defaultAffiliatePricePoints.filter((row) => this.isDefaultOutOfSync(row.pricePoint));
    },
    defaultAffiliatePricePoints() {
      return this.affiliatePricePointRows.filter((row) => {
        return !this.hasCommissionOverride(row.pricePoint);
      });
    },
    customAffiliatePricePoints() {
      return this.affiliatePricePointRows.filter((row) => {
        return this.hasCommissionOverride(row.pricePoint);
      });
    },
    affiliatePricePointRows() {
      return this.localModel.pricing_points.map((pricePoint, index) => {
        return { pricePoint, index };
      });
    },

    affiliateSettingsTabs() {
      const commissionTabs = [
        {
          title: this.$t('frontend_product_step9_commisions'),
          description: 'Default payout rules',
          value: 'commissions',
        },
        {
          title: this.$t('frontend_product_step9_commision_overrides'),
          description: 'Price-point exceptions',
          value: 'commissionOverrides',
        },
      ];
      if (!this.localModel.is_main) {
        return commissionTabs;
      }
      return commissionTabs.concat([
        {
          title: this.$t('frontend_product_step9_custom_landing'),
          description: `${(this.localModel.affiliate_landing_pages || []).length} page${(this.localModel.affiliate_landing_pages || []).length === 1 ? '' : 's'}`,
          value: 'affiliatesLandingPages',
        },
        {
          title: this.$t('frontend_product_step9_jv'),
          description: this.localModel.jv_page ? 'Linked' : 'Not set',
          value: 'jvblog',
        },
        {
          title: this.$t('frontend_product_step9_integrations'),
          description: 'On affiliate registration',
          value: 'integrations',
        },
        {
          title: this.$t('frontend_product_step9_data_share'),
          description: 'Data sharing and bonuses',
          value: 'data_sharing',
        },
      ]);
    },
    affiliateSettingsErrorTabs() {
      const affiliateSettingErrorTabs = [];

      if (this.$v.localModel.jv_page.$error) {
        affiliateSettingErrorTabs.push('jvblog');
      }

      if (this.$v.localModel.pricing_points.$error && this.affiliateSettingsTab == 'commissions') {
        affiliateSettingErrorTabs.push('commissions');
      }

      if (this.$v.localModel.pricing_points.$error && this.affiliateSettingsTab == 'commissionOverrides') {
        affiliateSettingErrorTabs.push('commissionOverrides');
      }

      if (this.$v.localModel.affiliate_landing_pages.$error) {
        affiliateSettingErrorTabs.push('affiliatesLandingPages');
      }
      return affiliateSettingErrorTabs;
    },
  },
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
  },
  created() { },
  mounted() {
    if (
      this.pricePointsTab == '' &&
      this.localModel.pricing_points.length > 0
    ) {
      this.pricePointsTab = `price_point_${this.localModel.pricing_points[0].id}`;
    }

    if (
      this.commissionOverridePricePointsTab == '' &&
      this.localModel.pricing_points.length > 0
    ) {
      this.commissionOverridePricePointsTab = `commission_override_price_point_${this.localModel.pricing_points[0].id}`;
    }

    if (
      this.landingPagesTab == '' &&
      this.localModel.affiliate_landing_pages.length > 0
    ) {
      this.landingPagesTab = `landing_page_${this.localModel.affiliate_landing_pages[0].id}`;
    }
  },
  methods: {
    setAffiliateProgramState(id) {
      this.localModel.affiliates_enabled = id;
      this.showAffiliateEditor = Boolean(id);
    },

    updateModel(value) {
      this.localModel = value;
    },
    toggleAffiliatePricePoint(index) {
      if (this.expandedAffiliatePricePointIndex === index) {
        this.expandedAffiliatePricePointIndex = null;
        return;
      }

      this.expandedAffiliatePricePointIndex = this.expandedAffiliatePricePointIndex === index ? null : index;
    },
    standardCommissionFor(pricePoint) {
      if (!pricePoint || !pricePoint.commission_details) {
        return null;
      }

      return pricePoint.commission_details.find((commission) => {
        return commission.type == 1;
      }) || null;
    },
    commissionOverridesFor(pricePoint) {
      return ((pricePoint && pricePoint.commission_details) || []).filter((commission) => {
        return commission.type != 1;
      });
    },
    hasCommissionOverride(pricePoint) {
      return this.commissionOverridesFor(pricePoint).length > 0;
    },
    isDefaultOutOfSync(pricePoint) {
      return Boolean(this.defaultCommissionTier)
        && !this.hasCommissionOverride(pricePoint)
        && !commissionRulesMatch(this.standardCommissionFor(pricePoint), this.defaultCommissionTier);
    },
    isApplyingDefault(pricePoint) {
      return !this.hasCommissionOverride(pricePoint) && !this.isDefaultOutOfSync(pricePoint);
    },
    activeCommissionTier(pricePoint) {
      return this.commissionOverridesFor(pricePoint)[0] || this.standardCommissionFor(pricePoint) || this.defaultCommissionTier;
    },
    commissionLabel(commissionTier, group, field) {
      if (!commissionTier || !commissionTier[group]) {
        return 'Not set';
      }

      const value = commissionTier[group][field];
      const type = commissionTier[group][`${field}_type`] || commissionTier[group].commission_type || 'percent';

      if (value === '' || value === null || typeof value === 'undefined') {
        return 'Not set';
      }

      return type === 'percent' ? `${value}%` : `$${value}`;
    },
    pricePointTierLabel(pricePoint, field) {
      const activeTier = this.activeCommissionTier(pricePoint);
      return this.commissionLabel(activeTier, 'sales_commission', field);
    },
    affiliatePricePointSummary(pricePoint) {
      const mode = this.hasCommissionOverride(pricePoint) ? 'Custom override' : 'Default commission';
      return `${mode} • Tier 1 ${this.pricePointTierLabel(pricePoint, 'commission')} • Tier 2 ${this.pricePointTierLabel(pricePoint, 'jv_broker_commission')}`;
    },
    commissionAvailabilityLabel(pricePoint) {
      const overrides = this.commissionOverridesFor(pricePoint);

      if (!overrides.length || !overrides[0].assigned_to) {
        return 'All affiliates';
      }

      return `${overrides[0].assigned_to.length} selected affiliate${overrides[0].assigned_to.length === 1 ? '' : 's'}`;
    },
    cloneDefaultCommissionOverride() {
      const source = this.defaultCommissionTier;
      const fallback = {
        sales_commission: {
          commission: 0,
          commission_type: 'percent',
          jv_broker_commission: 0,
          jv_broker_commission_type: 'percent',
        },
        trial_commission: {
          commission: 0,
          commission_type: 'percent',
          jv_broker_commission: 0,
          jv_broker_commission_type: 'percent',
        },
        rebill_commissions: [
          {
            commission: 50,
            no_of_payments: null,
            commission_type: 'percent',
            jv_broker_commission: 0,
            jv_broker_commission_type: 'percent',
          },
        ],
      };
      const base = source ? JSON.parse(JSON.stringify(source)) : fallback;

      return {
        ...base,
        id: Math.ceil(Math.random() * 100000),
        type: 2,
        name: 'Price point override',
        assigned_to: null,
      };
    },
    setAffiliateDefaultUsage(pricePoint, shouldUseDefault) {
      if (!pricePoint.commission_details) {
        this.$set(pricePoint, 'commission_details', []);
      }

      if (shouldUseDefault) {
        this.$set(pricePoint, 'commission_details', pricePoint.commission_details.filter((commission) => {
          return commission.type == 1;
        }));
        this.applyDefaultToPricePoint(pricePoint);
        return;
      }

      if (!this.hasCommissionOverride(pricePoint)) {
        const override = this.cloneDefaultCommissionOverride();
        const current = this.standardCommissionFor(pricePoint);
        if (current) {
          Object.assign(override, copyDefaultCommission(override, current));
        }
        pricePoint.commission_details.push(override);
      }
    },
    startInlineOverride(pricePoint) {
      this.setAffiliateDefaultUsage(pricePoint, false);
      this.expandedAffiliatePricePointIndex = this.localModel.pricing_points.indexOf(pricePoint);
    },
    updateInlineCommission(pricePoint, field, value) {
      const override = this.commissionOverridesFor(pricePoint)[0];
      if (!override || !override.sales_commission) return;
      this.$set(override.sales_commission, field, value === '' ? 0 : Number(value));
      if (override.trial_commission) this.$set(override.trial_commission, field, value === '' ? 0 : Number(value));
    },
    openDefaultCommissionModal() {
      this.ensureDefaultCommissionTier();
      this.defaultCommissionDraft = this.defaultCommissionTier
        ? JSON.parse(JSON.stringify(this.defaultCommissionTier)) : null;
      this.defaultCommissionComplex = Boolean(this.defaultCommissionDraft
        && this.defaultCommissionDraft.rebill_commissions.length > 1);
      this.showDefaultCommissionModal = true;
    },
    closeDefaultCommissionModal() {
      this.showDefaultCommissionModal = false;
      this.defaultCommissionDraft = null;
    },
    applyDefaultCommissionModal() {
      const pricePoint = this.localModel.pricing_points.find((item) => this.standardCommissionFor(item));
      if (!pricePoint || !this.defaultCommissionDraft) {
        return;
      }
      const index = pricePoint.commission_details.indexOf(this.standardCommissionFor(pricePoint));
      this.$set(pricePoint.commission_details, index, this.defaultCommissionDraft);
      this.applyDefaultToAllPricePoints();
      this.closeDefaultCommissionModal();
    },
    openDefaultBulkChoice() {
      if (this.customAffiliatePricePoints.length) {
        this.showDefaultBulkChoice = true;
        return;
      }
      this.applyDefaultToAllPricePoints(false);
    },
    applyDefaultToPricePoint(pricePoint) {
      if (!this.defaultCommissionTier || this.hasCommissionOverride(pricePoint)) {
        return;
      }
      const commissionDetails = pricePoint.commission_details || [];
      const current = this.standardCommissionFor(pricePoint);
      const updated = copyDefaultCommission(current, this.defaultCommissionTier);
      if (current) {
        this.$set(commissionDetails, commissionDetails.indexOf(current), updated);
      } else {
        this.$set(pricePoint, 'commission_details', [updated, ...commissionDetails]);
      }
    },
    applyDefaultToAllPricePoints(removeOverrides = false) {
      const targets = removeOverrides ? this.localModel.pricing_points : this.outOfSyncDefaultPricePoints.map(({ pricePoint }) => pricePoint);
      targets.forEach((pricePoint) => {
        if (removeOverrides && pricePoint.commission_details) {
          this.$set(pricePoint, 'commission_details', pricePoint.commission_details.filter((commission) => commission.type == 1));
        }
        this.applyDefaultToPricePoint(pricePoint);
      });
      this.showDefaultBulkChoice = false;
      const count = targets.length;
      this.affiliateDefaultNotice = `Default commissions are ready on ${count} price point${count === 1 ? '' : 's'}. Save this product to publish the payout changes.`;
    },
    setNoCommission(pricePoint) {
      if (!pricePoint.commission_details) {
        this.$set(pricePoint, 'commission_details', []);
      }
      const override = this.cloneDefaultCommissionOverride();
      const zero = (value) => ({ ...value, commission: 0, jv_broker_commission: 0 });
      override.sales_commission = zero(override.sales_commission);
      override.trial_commission = zero(override.trial_commission);
      override.rebill_commissions = (override.rebill_commissions || []).map(zero);
      this.$set(pricePoint, 'commission_details', pricePoint.commission_details.filter((commission) => commission.type == 1).concat(override));
      this.expandedAffiliatePricePointIndex = this.localModel.pricing_points.indexOf(pricePoint);
      this.affiliateDefaultNotice = `Commissions are disabled for ${pricePoint.name}. Save this product to publish the change.`;
    },
    ensureDefaultCommissionTier() {
      if (this.defaultCommissionTier || !this.localModel.pricing_points.length) {
        return;
      }

      const pricePoint = this.localModel.pricing_points[0];
      if (!pricePoint.commission_details) {
        this.$set(pricePoint, 'commission_details', []);
      }

      pricePoint.commission_details.push({
        ...this.cloneDefaultCommissionOverride(),
        type: 1,
        name: 'Standard affiliate rule',
      });
    },
    addDefaultRecurringCommission() {
      this.defaultCommissionDraft.rebill_commissions.push({
        commission: 50,
        no_of_payments: null,
        commission_type: 'percent',
        jv_broker_commission: 0,
        jv_broker_commission_type: 'percent',
      });
    },
    stopDefaultRecurringCommissionAfterSchedule() {
      this.defaultCommissionDraft.rebill_commissions.push({
        commission: 0,
        no_of_payments: null,
        commission_type: 'percent',
        jv_broker_commission: 0,
        jv_broker_commission_type: 'percent',
      });
    },
    deleteDefaultRecurringCommission(index) {
      this.defaultCommissionDraft.rebill_commissions.splice(index, 1);
    },
    previousPaymentCount(rebillCommissions, commissionDetailIndex) {
      return rebillCommissions.slice(0, commissionDetailIndex).reduce((total, item) => {
        return total + (parseInt(item.no_of_payments, 10) || 0);
      }, 0);
    },
    recurringRangeLabel(rebillCommissions, commissionDetailIndex) {
      const start = this.previousPaymentCount(rebillCommissions, commissionDetailIndex) + 1;
      const current = rebillCommissions[commissionDetailIndex];
      const count = parseInt(current.no_of_payments, 10);

      if (!count) {
        return `Payment ${start}+`;
      }

      const end = start + count - 1;
      return start === end ? `Payment ${start}` : `Payments ${start}-${end}`;
    },
    recurringRangeDescription(rebillCommissions, commissionDetailIndex) {
      const current = rebillCommissions[commissionDetailIndex];
      const count = parseInt(current.no_of_payments, 10);

      if (!count) {
        return 'All remaining payments';
      }

      return `Next ${count} payment${count === 1 ? '' : 's'}`;
    },
    openAffiliateAdvanced(tab = 'commissions') {
      this.affiliateSettingsTab = tab;
      this.editorSection = 'advanced';
      this.$nextTick(() => {
        if (this.$refs.affiliateAdvancedPanel) {
          this.$refs.affiliateAdvancedPanel.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    },
    openPricePointOverride(index) {
      this.setAffiliateDefaultUsage(this.localModel.pricing_points[index], false);
      this.selectedPricePointIndex = index;
      this.commissionOverridePricePointsTab = `commission_override_price_point_${this.localModel.pricing_points[index].id}`;
      this.openAffiliateAdvanced('commissionOverrides');
    },
    openCustomLandingPagesAffModal(index) {
      this.selectedLandingPageIndex = index;
      this.showCustomLandingPagesAffiliatesModal = true;
    },
    applyLandingPageAffiliates(affiliates) {
      if (this.selectedLandingPage) this.$set(this.selectedLandingPage, 'assigned_to', affiliates);
    },

    openCommissionOverridesAffModal(index, commissionTierIndex) {
      this.selectedPricePointIndex = index;
      this.selectedCommissionTierIndex = commissionTierIndex;
      this.showCommissionOverridesAffiliatesModal = true;
    },

    addedAffiliateToCommissionTier(affiliate) {
  
      if (
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details.filter((obj) => {
          return obj.type != 1;
        })[this.selectedCommissionTierIndex].assigned_to == null
      ) {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details.filter((obj) => {
          return obj.type != 1;
        })[this.selectedCommissionTierIndex].assigned_to = [];

        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details
          .filter((obj) => {
            return obj.type != 1;
          })
        [this.selectedCommissionTierIndex].assigned_to.push(affiliate.id);
      } else if (
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details
          .filter((obj) => {
            return obj.type != 1;
          })
        [this.selectedCommissionTierIndex].assigned_to.indexOf(
          affiliate.id
        ) === -1
      ) {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].commission_details
          .filter((obj) => {
            return obj.type != 1;
          })
        [this.selectedCommissionTierIndex].assigned_to.push(affiliate.id);
      }
    },

    removedAffiliateFromCommissionTier(affiliateId) {
      const removedAffiliate = this.localModel.pricing_points[
        this.selectedPricePointIndex
      ].commission_details
        .filter((obj) => {
          return obj.type != 1;
        })
      [this.selectedCommissionTierIndex].assigned_to.indexOf(affiliateId);

      this.localModel.pricing_points[
        this.selectedPricePointIndex
      ].commission_details
        .filter((obj) => {
          return obj.type != 1;
        })
      [this.selectedCommissionTierIndex].assigned_to.splice(
        removedAffiliate,
        1
      );
    },

    showAffiliateRegistrationIntegrationBox(index) {
      this.integrationType = '';
      this.showAffiliateRegistrationIntegrationModal = true;
    },

    setValue(value) {
      this.integrationType = value;
      this.pluginId = '';
      this.componentConfigValues = [];
      this.configMenuKey = this.configMenuKey + 1;
    },

    addAffiliateRegistrationIntegration(data) {
      const integrationConfig = data.config;
      const self = this;

      const integrationInfo = [];

      for (const index in integrationConfig) {
        const value = integrationConfig[index];

        if (value) {
          self.pluginMenuItems.forEach((menuValue, menuIndex) => {
            if (menuValue.key == index) {
              const { options } = menuValue;

              const objChecker = options.some((val) => {
                return typeof val === 'object';
              });

              let correspondingOption = value;
              if (objChecker) {
                correspondingOption = options.find((o) => o.value === value);
              }

              const singleIntegrationField = {};
              singleIntegrationField.index = index;
              singleIntegrationField.value = value;
              singleIntegrationField.label = menuValue.label;

              if (objChecker) {
                singleIntegrationField.name = correspondingOption.text;
              } else {
                singleIntegrationField.name = value;
              }

              integrationInfo.push(singleIntegrationField);
            }
          });
        }
      }

      const completeIntegrationInfo = {};
      completeIntegrationInfo.platform = this.integrationType.split('-')[0];
      completeIntegrationInfo.config = integrationInfo;

      if (this.localModel.affiliate_registration_integrations) {
        this.localModel.affiliate_registration_integrations.push(
          completeIntegrationInfo
        );
      } else {
        this.localModel.affiliate_registration_integrations = [];
        this.localModel.affiliate_registration_integrations.push(
          completeIntegrationInfo
        );
      }

      this.showAffiliateRegistrationIntegrationModal = false;
    },

    configMenuItemsUpdated(data) {
      this.pluginMenuItems = data.menuItems;
    },

    onAffiliateChange(affiliateLandingPageIndex) {
      if (
        this.localModel.affiliate_landing_pages[affiliateLandingPageIndex]
          .assigned_to &&
        this.localModel.affiliate_landing_pages[affiliateLandingPageIndex]
          .assigned_to.length > 0
      ) {
        const lastAddedAffiliateId =
          this.localModel.affiliate_landing_pages[affiliateLandingPageIndex]
            .assigned_to[
            this.localModel.affiliate_landing_pages[affiliateLandingPageIndex]
              .assigned_to.length - 1
          ].id;

        if (lastAddedAffiliateId == 0) {
          this.localModel.affiliate_landing_pages[
            affiliateLandingPageIndex
          ].assigned_to =
            this.localModel.affiliate_landing_pages[
              affiliateLandingPageIndex
            ].assigned_to.slice(-1);
        } else {
          this.localModel.affiliate_landing_pages[
            affiliateLandingPageIndex
          ].assigned_to = this.localModel.affiliate_landing_pages[
            affiliateLandingPageIndex
          ].assigned_to.filter((obj) => {
            return obj.id !== 0;
          });
        }
      }
    },

    deleteAffiliateFromCommissionTier(
      index,
      commissionTierIndex,
      commissionTierAffiliate
    ) {
      const commissionOverrideTier = this.localModel.pricing_points[
        index
      ].commission_details.filter((obj) => {
        return obj.type != 1;
      })[commissionTierIndex];

      if (commissionOverrideTier.assigned_to) {
        commissionOverrideTier.assigned_to =
          commissionOverrideTier.assigned_to.filter(
            (item) => item !== commissionTierAffiliate
          );
      }
    },

    affiliatesForThisPricePoint(index) {
      let assignedAffiliatesArray = [];
      this.localModel.pricing_points[index].commission_details
        .filter((obj) => {
          return obj.type != 1 && obj.assigned_to != null;
        })
        .forEach((arrayItem) => {
          assignedAffiliatesArray = assignedAffiliatesArray.concat(
            arrayItem.assigned_to
          );
        });

      return this.affiliatesForCommissionOverrides.filter((obj) => {
        return !assignedAffiliatesArray.includes(obj.value);
      });
    },

    addAffiliatesToCommissionOverride(index, commissionTierIndex) {
      const commissionOverrideTier = this.localModel.pricing_points[
        index
      ].commission_details.filter((obj) => {
        return obj.type != 1;
      })[commissionTierIndex];

      if (commissionOverrideTier.assigned_to) {
        commissionOverrideTier.assigned_to.push(
          this.commissionOverrideSelectedAffiliate
        );
      } else {
        commissionOverrideTier.assigned_to = [];
        commissionOverrideTier.assigned_to.push(
          this.commissionOverrideSelectedAffiliate
        );
      }

      // this.localModel.pricing_points[index].commission_details[commissionTierIndex].assigned_to.push(this.commissionOverrideSelectedAffiliate);
    },

    handleAffiliateSettingsTabChange(newTab) {
      this.affiliateSettingsTab = newTab;
    },

    getTrialCommissionDetails(details) {
      const trialCommissionObj = details.filter((elem) => {
        if (elem.type == '0' || elem.type == 0) {
          return elem;
        }
      });

      if (trialCommissionObj.length > 0) {
        return trialCommissionObj[0];
      }
      return false;
    },
    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    commissionsNavigate(navigateMethod, index) {
      this.$refs.commissionsWizard.changeTab(this.commissionsIndex, index);
    },

    affiliateLandingPageNavigate(navigateMethod, index) {
      this.$refs.affiliateLandingPagesWizard.changeTab(
        this.affiliateLandingPageIndex,
        index
      );
    },
    handleCreateAffiliateLandingPage() {
      this.createAffiliateLandingPage();
    },
    async createAffiliateLandingPage() {
      let newLandingPageId = this.generateNumber();
      const existingIds = this.localModel.affiliate_landing_pages.map(page => Number(page.id));
      while (existingIds.includes(newLandingPageId) || newLandingPageId === 99999) newLandingPageId += 1;
      this.localModel.affiliate_landing_pages.push({
        id: newLandingPageId,
        landing_page: '',
        description: '',
        assigned_to: [
          {
            id: 0,
            name: this.$t('frontend_affiliates_all_affiliates'),
          },
        ],
      });

      this.landingPagesTab = `landing_page_${newLandingPageId}`;
      // Keep the blank page as a draft until the vendor completes and saves it.
    },

    affiliateName(affiliateId) {
      if (affiliateId) {
        const affiliate = this.affiliatesForCommissionOverrides.find((obj) => {
          return obj.value === affiliateId;
        });

        return affiliate.text;
      }
      return this.$t('frontend_affiliates_all_affiliates');
    },
    openDeleteAffiliateLandingPageConfirmModal(index) {
      this.affiliateLandingPageToDeleteIndex = index;
      this.showDeleteAffiliateLandingPageConfirm = true;
    },
    async deleteAffiliateLandingPage() {
      this.localModel.affiliate_landing_pages.splice(this.affiliateLandingPageToDeleteIndex, 1);

      const nextOpenIndex = this.affiliateLandingPageToDeleteIndex - 1;
      const nextOpenLandingPageId =
        this.localModel.affiliate_landing_pages[nextOpenIndex].id;
      this.landingPagesTab = `landing_page_${nextOpenLandingPageId}`;
        this.$emit('saveProdInfo');
    },

    onCommissionOverrideAffiliateChange() { },

    onLandingPageAffiliateChange() {
      if (
        this.localModel.affiliate_landing_pages[this.affiliateLandingPageIndex]
          .assigned_to &&
        this.localModel.affiliate_landing_pages[this.affiliateLandingPageIndex]
          .assigned_to.length > 0
      ) {
        const lastAddedAffiliateId =
          this.localModel.affiliate_landing_pages[
            this.affiliateLandingPageIndex
          ].assigned_to[
            this.localModel.affiliate_landing_pages[
              this.affiliateLandingPageIndex
            ].assigned_to.length - 1
          ].id;

        if (lastAddedAffiliateId == 0) {
          this.localModel.affiliate_landing_pages[
            this.affiliateLandingPageIndex
          ].assigned_to =
            this.localModel.affiliate_landing_pages[
              this.affiliateLandingPageIndex
            ].assigned_to.slice(-1);
        } else {
          this.localModel.affiliate_landing_pages[
            this.affiliateLandingPageIndex
          ].assigned_to = this.localModel.affiliate_landing_pages[
            this.affiliateLandingPageIndex
          ].assigned_to.filter((obj) => {
            return obj.id !== 0;
          });
        }
      }
    },

    generateNumber() {
      const min = 1;
      const max = 10000;
      return Math.floor(Math.random() * (max - min + 1) + min);
    },
    openDeleteAffiliateRegistrationIntegrationConfirmModal(affiliateRegistrationIndex) {
      this.affiliateRegistrationIndex = affiliateRegistrationIndex;
      this.showDeleteAffiliateRegistrationIntegrationConfirm = true;
    },
    async deleteAffiliateRegistrationIntegration() {
        this.localModel.affiliate_registration_integrations.splice(
          this.affiliateRegistrationIndex,
          1
        );
    },
    goToSetupGateways() {
      const routeData = this.$router.resolve({ name: 'vendor_settings' });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>
<style>
.gs-affiliates-step {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.gs-affiliates-step__drawer .gs-drawer__panel { width: min(72vw, 1120px); }

.gs-affiliates-step__workspace-tabs {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--gs-border, #e2e8f0);
  background: #fff;
}

/* The drawer body carries its own top padding, so a stuck tab bar needs to
   paint over that strip too or scrolled content stays visible above it. */
.gs-affiliates-step__workspace-tabs::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 32px;
  background: #fff;
}

.gs-affiliates-step__workspace-tabs button {
  min-width: 0;
  padding: 16px 18px;
  text-align: left;
  background: var(--gs-surface-subtle, #f8fafc);
  border: 2px solid var(--gs-border, #e2e8f0);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-affiliates-step__workspace-tabs button.is-active {
  background: var(--gs-active-soft, #eff6ff);
  border-color: var(--gs-active, #2563eb);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-affiliates-step__workspace-tabs button:focus-visible {
  outline: 3px solid var(--gs-active);
  outline-offset: 3px;
}

.gs-affiliates-step__workspace-body {
  min-width: 0;
}

.gs-affiliates-step__commission-pane,
.gs-affiliates-step__advanced {
  min-width: 0;
}

.gs-affiliates-step__workspace-tabs strong,
.gs-affiliates-step__workspace-tabs span {
  display: block;
}

.gs-affiliates-step__workspace-tabs strong {
  color: var(--gs-foreground);
  font-size: 16px;
}

.gs-affiliates-step__workspace-tabs span {
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}


.gs-affiliates-step__summary h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}


.gs-affiliates-step__cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.gs-affiliates-step__card {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 12px;
  align-items: flex-start;
  min-height: 82px;
  padding: 14px;
  text-align: left;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  cursor: pointer;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.gs-affiliates-step__card.is-active {
  background: var(--gs-active-soft) !important;
  border-color: var(--gs-active) !important;
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm) !important;
}

.gs-affiliates-step__radio {
  position: relative;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  background: #ffffff;
  border: 2px solid var(--gs-border);
  border-radius: 999px;
}

.gs-affiliates-step__card.is-active .gs-affiliates-step__radio {
  border-color: var(--gs-active);
}

.gs-affiliates-step__card.is-active .gs-affiliates-step__radio::after {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 8px;
  height: 8px;
  background: var(--gs-active);
  border-radius: 999px;
  content: "";
}

.gs-affiliates-step__card-copy {
  display: block;
  min-width: 0;
}

.gs-affiliates-step__card strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-affiliates-step__card span {
  display: block;
  margin-top: 6px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.35;
}

.gs-affiliates-step__manage-button.gs-button {
  display: flex;
  margin: 14px auto 0;
}


.gs-affiliates-step__default-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
}

.gs-affiliates-step__processing-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-affiliates-step__processing-row strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-affiliates-step__processing-row span {
  display: block;
  margin-top: 5px;
}

.gs-affiliates-step__processing-row span,
.gs-affiliates-step__default-card p,
.gs-affiliates-step__list-head span,
.gs-affiliates-step__summary-line,
.gs-affiliates-step__body-head p {
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.4;
}

.gs-affiliates-step__notice {
  margin: 0;
  padding: 12px 14px;
  color: var(--gs-foreground);
  font-size: 14px;
  line-height: 1.45;
  background: #fff8eb;
  border: 1px solid #ffe3ae;
  border-radius: var(--gs-radius-sm);
}

.gs-affiliates-step__notice span {
  color: #c87900;
  font-weight: 600;
}

.gs-affiliates-step__notice button {
  color: var(--gs-primary);
  font-weight: 600;
  text-decoration: underline;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-affiliates-step__default-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, .75fr) auto;
  align-items: center;
  gap: 18px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-affiliates-step__default-card small,
.gs-affiliates-step__detail-grid small {
  display: block;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  text-transform: uppercase;
}

.gs-affiliates-step__default-card > div > strong {
  display: block;
  margin-top: 5px;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 600;
}

.gs-affiliates-step__default-card p {
  margin: 6px 0 0;
}

.gs-affiliates-step__default-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.gs-affiliates-step__default-stats span,
.gs-affiliates-step__detail-grid div {
  min-height: 72px;
  padding: 12px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-affiliates-step__default-stats strong,
.gs-affiliates-step__detail-grid strong {
  display: block;
  margin-top: 6px;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 600;
}

.gs-affiliates-step__price-points {
  margin-top: 18px;
}

.gs-affiliates-step__list-head {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: baseline;
  column-gap: 16px;
  margin-bottom: 10px;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gs-affiliates-step__list-head strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-affiliates-step__inline-notice {
  margin: 0 0 12px;
  padding: 10px 12px;
  color: var(--gs-primary);
  font-size: 13px;
  line-height: 1.4;
  background: var(--gs-primary-soft);
  border: 1px solid var(--gs-primary-soft);
  border-radius: var(--gs-radius-sm);
}

.gs-affiliates-step__sync-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 16px;
  background: #fff8eb;
  border: 1px solid #f3cb79;
  border-radius: var(--gs-radius-md);
}

.gs-affiliates-step__sync-notice strong,
.gs-affiliates-step__sync-notice span { display: block; }
.gs-affiliates-step__sync-notice strong { color: var(--gs-foreground); }
.gs-affiliates-step__sync-notice span { margin-top: 4px; color: var(--gs-muted-foreground); font-size: 13px; }
.gs-affiliates-step__sync-notice .gs-button { flex: 0 0 auto; }

.gs-affiliates-step__group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gs-affiliates-step__group + .gs-affiliates-step__group {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gs-affiliates-step__group-label {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.gs-affiliates-step__group-label strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-affiliates-step__group-label span {
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-affiliates-step__accordion {
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  overflow: hidden;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.gs-affiliates-step__accordion.is-open {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-affiliates-step__accordion-head {
  display: grid;
  grid-template-columns: 18px minmax(220px, 1fr) minmax(150px, auto) max-content 24px;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 70px;
  padding: 10px 16px;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-affiliates-step__accordion-head:focus-visible {
  outline: 3px solid var(--gs-active-shadow);
  outline-offset: -3px;
}

.gs-affiliates-step__status-dot {
  width: 12px;
  height: 12px;
  background: #ffffff;
  border: 2px solid var(--gs-border);
  border-radius: 999px;
}

.gs-affiliates-step__accordion.has-override .gs-affiliates-step__status-dot {
  background: var(--gs-success);
  border-color: var(--gs-success);
}

.gs-affiliates-step__title-block,
.gs-affiliates-step__name,
.gs-affiliates-step__summary-line {
  min-width: 0;
}

.gs-affiliates-step__name {
  display: block;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-affiliates-step__summary-line {
  display: block;
  margin-top: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-affiliates-step__pill {
  padding: 8px 10px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
  white-space: nowrap;
}

.gs-affiliates-step__accordion.has-override .gs-affiliates-step__pill {
  color: var(--gs-success);
  background: var(--gs-success-soft);
}

.gs-affiliates-step__pill.is-unsynced {
  color: #8b5312;
  background: #fff0cf;
}

.gs-affiliates-step__chevron {
  color: var(--gs-muted-foreground);
  font-size: 20px;
  text-align: right;
}

.gs-affiliates-step__accordion-body {
  padding: 18px 16px 18px 46px;
  background: #ffffff;
  border-top: 1px solid var(--gs-border);
}

.gs-affiliates-step__body-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.gs-affiliates-step__body-head strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-affiliates-step__body-head p {
  margin: 5px 0 0;
}

.gs-affiliates-step__default-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-affiliates-step__default-toggle--row {
  justify-self: end;
}

.gs-affiliates-step__switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  background: var(--gs-border);
  border-radius: 999px;
  transition: background .18s ease;
}

.gs-affiliates-step__switch::after {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(23, 27, 63, .18);
  transition: transform .18s ease;
  content: "";
}

.gs-affiliates-step__switch.is-on {
  background: var(--gs-primary);
}

.gs-affiliates-step__switch.is-on::after {
  transform: translateX(22px);
}

.gs-affiliates-step__detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.gs-affiliates-step__override-action {
  margin-top: 14px;
}

.gs-affiliates-step__no-commission {
  display: block;
  margin-top: 12px;
  padding: 0;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 650;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-decoration: underline;
}

.gs-affiliates-step__inline-override {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  align-items: end;
  gap: 10px;
  margin-top: 14px;
  padding: 12px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-affiliates-step__inline-override label span {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
}

.gs-affiliates-step__inline-override input {
  width: 100%;
  min-height: 36px;
  padding: 0 10px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-affiliates-step__override-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
}

.gs-affiliates-step__bulk-choice {
  padding: 4px 0 8px;
}

.gs-affiliates-step__bulk-choice p {
  margin: 0;
  color: var(--gs-muted-foreground);
  line-height: 1.5;
}

.gs-affiliates-step__bulk-choice-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.gs-affiliates-step__advanced {
  display: grid;
  grid-template-columns: 208px minmax(0, 1fr);
  align-items: start;
  gap: 24px;
  min-height: 420px;
}

/* Docked local rail: the step's own navigation, beside its content. */
.gs-affiliates-step__rail {
  position: sticky;
  top: 118px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 16px;
  border-right: 1px solid var(--gs-border);
}

.gs-affiliates-step__rail-item {
  position: relative;
  display: block;
  width: 100%;
  padding: 10px 12px 10px 14px;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
  transition: background .15s ease, color .15s ease;
}

.gs-affiliates-step__rail-item::before {
  position: absolute;
  top: 8px;
  bottom: 8px;
  left: 0;
  width: 3px;
  background: transparent;
  border-radius: 3px;
  content: "";
}

.gs-affiliates-step__rail-item:hover {
  background: var(--gs-surface-subtle);
}

.gs-affiliates-step__rail-item:focus-visible {
  outline: 3px solid var(--gs-ring);
  outline-offset: 1px;
}

.gs-affiliates-step__rail-item strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.gs-affiliates-step__rail-item span {
  display: block;
  margin-top: 2px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  line-height: 1.35;
}

.gs-affiliates-step__rail-item.is-active {
  background: var(--gs-active-soft);
}

.gs-affiliates-step__rail-item.is-active::before {
  background: var(--gs-active);
}

.gs-affiliates-step__rail-item.is-active strong {
  color: var(--gs-active);
}

.gs-affiliates-step__rail-item.has-error strong::after {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-left: 6px;
  vertical-align: middle;
  background: var(--gs-danger);
  border-radius: 999px;
  content: "";
}

.gs-affiliates-step__advanced-content {
  min-width: 0;
}

.gs-affiliates-step__modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  background: rgba(23, 27, 63, .36);
}

.gs-affiliates-step__default-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 2147483001;
  display: flex;
  flex-direction: column;
  width: min(1080px, calc(100vw - 32px));
  max-height: min(92vh, 960px);
  overflow: hidden;
  padding: 0;
  background: #ffffff;
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-lg);
  transform: translate(-50%, -50%);
  box-sizing: border-box;
}

.gs-affiliates-step__default-modal header,
.gs-affiliates-step__default-modal footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px;
  border-bottom: 1px solid var(--gs-border);
}

.gs-affiliates-step__default-modal footer {
  justify-content: flex-end;
  border-top: 1px solid var(--gs-border);
  border-bottom: 0;
}

.gs-affiliates-step__default-modal h3 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 650;
}

.gs-affiliates-step__default-modal p {
  margin: 6px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-affiliates-step__default-modal header > button {
  width: 34px;
  height: 34px;
  color: var(--gs-muted-foreground);
  font-size: 24px;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-affiliates-step__modal-content {
  display: flex;
  min-height: 0;
  overflow-y: auto;
  flex-direction: column;
  gap: 16px;
  padding: 24px 28px;
}

.gs-affiliates-step__modal-card {
  padding: 20px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-affiliates-step__modal-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.gs-affiliates-step__complex-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.gs-affiliates-step__modal-card-head strong,
.gs-affiliates-step__modal-row strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-affiliates-step__modal-card-head > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: var(--gs-primary);
  font-size: 13px;
  font-weight: 700;
  background: var(--gs-primary-soft);
  border-radius: 999px;
  cursor: help;
}

.gs-affiliates-step__modal-grid,
.gs-affiliates-step__modal-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.gs-affiliates-step__modal-schedule {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.gs-affiliates-step__modal-row {
  grid-template-columns: minmax(120px, .8fr) 116px minmax(230px, 1.2fr) minmax(230px, 1.2fr) 40px;
  align-items: end;
  margin-top: 0;
  padding: 14px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-affiliates-step__default-modal label span {
  display: block;
  margin-bottom: 7px;
  color: var(--gs-foreground);
  font-size: 13px;
  font-weight: 600;
}

.gs-affiliates-step__default-modal input,
.gs-affiliates-step__default-modal select {
  width: 100%;
  min-width: 0;
  min-height: 40px;
  padding: 0 12px;
  color: var(--gs-foreground);
  font-size: 15px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  outline: none;
}

.gs-affiliates-step__default-modal input:focus,
.gs-affiliates-step__default-modal select:focus {
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-affiliates-step__modal-pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(110px, .8fr);
  gap: 8px;
  min-width: 0;
}

.gs-affiliates-step__modal-row > *,
.gs-affiliates-step__modal-grid > *,
.gs-affiliates-step__modal-card-head > * { min-width: 0; }

.gs-affiliates-step__modal-row small {
  display: block;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.gs-affiliates-step__modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

@media (max-width: 1050px) {
  .gs-affiliates-step__modal-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .gs-affiliates-step__default-card,
  .gs-affiliates-step__body-head {
    grid-template-columns: 1fr;
  }

  .gs-affiliates-step__detail-grid,
  .gs-affiliates-step__default-stats {
    grid-template-columns: 1fr;
  }

  .gs-affiliates-step__accordion-head {
    grid-template-columns: 1fr;
  }

  .gs-affiliates-step__default-toggle--row {
    justify-self: start;
  }

  .gs-affiliates-step__advanced {
    grid-template-columns: 1fr;
  }

  .gs-affiliates-step__rail {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    padding: 0 0 8px;
    border-right: 0;
    border-bottom: 1px solid var(--gs-border);
  }

  .gs-affiliates-step__rail-item {
    flex: 0 0 auto;
    width: auto;
  }
}

@media (max-width: 640px) {
  .gs-affiliates-step__drawer .gs-drawer__panel { width: 100%; }
  .gs-affiliates-step__workspace-tabs,
  .gs-affiliates-step__modal-grid,
  .gs-affiliates-step__modal-row { grid-template-columns: 1fr; }
  .gs-affiliates-step__inline-override { grid-template-columns: 1fr; }
  .gs-affiliates-step__sync-notice { align-items: stretch; flex-direction: column; }
  .gs-affiliates-step__default-modal { width: calc(100vw - 16px); max-height: calc(100vh - 16px); }
  .gs-affiliates-step__default-modal header,
  .gs-affiliates-step__default-modal footer,
  .gs-affiliates-step__modal-content { padding: 16px; }
}

/* vue-multiselect (third-party) highlight colours. */
.multiselect__option--highlight,
.multiselect__option--highlight:after,
.multiselect__option--selected.multiselect__option--highlight,
.multiselect__option--selected.multiselect__option--highlight:after,
.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background-color: var(--gs-primary) !important;
}
</style>
